/**
 * Sandbox environments — the two axes the dev environment picker offers.
 *
 * An environment is a database AND the chatbot that answers for it, but the two are
 * chosen SEPARATELY: the picker lists one row per database and pairs the row you select
 * with the chatbot you pick, so every combination is reachable and no database is ever
 * listed twice. Applying still moves both halves at once ({@link applyEnvironment}) —
 * that is the drift this module exists to prevent. The two lists come from two
 * independent sources:
 *
 *   databases — the `get-branch-list` edge function on the parent project
 *   chatbots  — {@link DEVELOP_CHATBOT}, plus `GET /_sandboxes` on the sandbox router
 *
 * The develop chatbot is a constant rather than a fetched entry because the router is
 * the part that can be missing (its own hostname, its own tunnel rule), and a list
 * assembled only from it degrades to nothing.
 *
 * Which chatbot suits which database is information, not a filter. chat-dev derives the
 * project per request from the caller's token, so it answers for any of them; a sandbox
 * chatbot serves the one it booted on. Both pair with anything the user asks for, and
 * {@link pairingMismatch} names the pairs that are a mismatch rather than hiding them.
 */

import {
  CHATBOT_OVERRIDE_KEY,
  SUPABASE_OVERRIDE_KEY,
  isChatbotOverrideEnabled,
  isSupabaseProjectUrl,
  normalizeChatbotUrl,
  resolveChatbotBaseUrl,
  supabaseProjectRef,
  type SupabaseBranchOverride,
} from "@/lib/chatbotBaseUrl";

/** The router that fronts every sandbox chatbot; also serves the sandbox listing. */
const SANDBOX_ROUTER_ORIGIN = "https://chat-sandbox.franklin.ee";
const SANDBOX_LIST_PATH = "/_sandboxes";
const CHATBOT_HEALTH_PATH = "/health";
const REQUEST_TIMEOUT_MS = 8000;

/** A Supabase preview branch, as `get-branch-list` returns it. */
export interface BranchSummary {
  id: string;
  name: string;
  gitBranch: string;
  prNumber: number | null;
  status: string;
  isDefault: boolean;
  persistent: boolean;
  credentials: { url: string; anonKey: string };
}

/** A chatbot the picker can point the app at — the develop one, or a sandbox chatbot
 * in the shape the router's `/_sandboxes` returns. */
export interface ChatbotTarget {
  name: string;
  /** Base URL, validated against the chatbot host allowlist. */
  url: string;
  /** The project it booted on, and — since it is present only on a chatbot that serves
   * that one project — what pins it. The develop chatbot reports one over `/health` but
   * routes per request instead of serving it, so it deliberately claims none here. */
  supabase_ref?: string;
  sha?: string | null;
}

/**
 * The develop chatbot — what every preview build is wired to, and the picker's default.
 *
 * Hard-coded on purpose. It is the one chatbot that is always there, so it must not
 * depend on the sandbox router: with `/_sandboxes` unreachable — or simply empty, which
 * it is until a sandbox chatbot is deployed — this entry is the whole chatbot list, and
 * the picker still offers a real choice instead of an empty list next to a URL box.
 */
export const DEVELOP_CHATBOT: ChatbotTarget = {
  name: "develop",
  url: "https://chat-dev.franklin.ee",
};

/** A database the app can be pointed at — one row of the picker's list. */
export interface DatabaseTarget {
  /** Stable list key. */
  key: string;
  /** Display name — the Supabase branch name. */
  name: string;
  /** The Supabase project ref; null when the branch reports no API URL to read it from. */
  ref: string | null;
  branch: BranchSummary;
}

/** A database and the chatbot chosen for it — what {@link applyEnvironment} moves to.
 * Never half of one: a pair with no chatbot could move the database alone. */
export interface Environment extends DatabaseTarget {
  chatbot: ChatbotTarget;
}

/** The chatbot's unauthenticated health endpoint (Contract 1). Fields are optional
 * so a chatbot that predates any of them degrades instead of throwing. */
export interface ChatbotHealth {
  status?: string;
  environment?: string;
  supabase_ref?: string;
  sandbox?: string | null;
  sha?: string | null;
  /** The chatbot's own env is inconsistent: its Supabase URL and pooler URL name different projects. */
  ref_mismatch?: boolean;
  /**
   * Whether this pod resolves its database from the caller's access token rather than
   * serving only the project it booted with. Reported by the chatbot because it depends
   * on cluster secrets the app cannot see. Absent on a chatbot older than the field —
   * treat that as "not known", never as false.
   */
  routes_per_token?: boolean;
}

/** Verdict of comparing the chatbot's database against the app's. */
export type Pairing =
  | { kind: "match"; ref: string }
  /** Pinned to a database that is not the app's — its answers describe something else. */
  | { kind: "mismatch"; chatbotRef: string; appRef: string }
  /** Not pinned: it booted on `chatbotRef` but derives the project from the caller's token. */
  | { kind: "per-request"; chatbotRef: string; appRef: string }
  | { kind: "self-mismatch"; chatbotRef: string }
  | { kind: "unknown"; reason: string };

/** A non-2xx response. Carries the status so callers can tell 404 (endpoint absent)
 * from everything else without parsing a message string. */
export class HttpError extends Error {
  constructor(
    readonly status: number,
    statusText: string
  ) {
    super(`${status} ${statusText}`.trim());
    this.name = "HttpError";
  }
}

const JSON_HEADERS = { Accept: "application/json" };

/**
 * A response's JSON body, or an {@link HttpError} carrying the status.
 *
 * Deliberately takes the response, not a URL: no function in this module accepts a URL
 * and fetches it, so what this module can talk to is the three call sites below — each
 * building its target from a constant origin or from a host `normalizeChatbotUrl` has
 * accepted — and not whatever a caller passes in.
 */
async function readJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw new HttpError(response.status, response.statusText);
  }
  return (await response.json()) as T;
}

/** Run `request` with an abort after {@link REQUEST_TIMEOUT_MS}, chained to `signal`. */
async function withTimeout<T>(
  signal: AbortSignal | undefined,
  request: (signal: AbortSignal) => Promise<T>
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const forward = () => controller.abort();
  signal?.addEventListener("abort", forward);
  try {
    return await request(controller.signal);
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", forward);
  }
}

/**
 * The Supabase preview branches.
 *
 * Deliberately the BUILD-TIME project URL, never the overridden one: branches are
 * managed on the parent project, and only it has the management token configured.
 */
export async function fetchBranches(signal?: AbortSignal): Promise<BranchSummary[]> {
  const data = await withTimeout(signal, async (s) =>
    readJson<{ branches?: BranchSummary[] }>(
      await fetch(
        `${import.meta.env.VITE_SUPABASE_METADATA_URL || import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-branch-list`,
        {
          signal: s,
          headers: JSON_HEADERS,
        }
      )
    )
  );
  return data.branches ?? [];
}

/** The sandbox chatbots currently running, from the router (Contract 2). */
export async function fetchSandboxes(signal?: AbortSignal): Promise<ChatbotTarget[]> {
  const data = await withTimeout(signal, async (s) =>
    readJson<{ sandboxes?: ChatbotTarget[] }>(
      await fetch(`${SANDBOX_ROUTER_ORIGIN}${SANDBOX_LIST_PATH}`, {
        signal: s,
        headers: JSON_HEADERS,
      })
    )
  );
  return data.sandboxes ?? [];
}

/**
 * The self-report of the chatbot the app is talking to. A 404 means it predates the
 * endpoint — see {@link describePairing}.
 *
 * Takes no URL. The only chatbot worth probing is the one in use, so this resolves it
 * itself and puts it through the same allowlist as the override: a host we would refuse
 * to send an access token to is one we have no reason to probe either, and no caller can
 * aim this at a URL of its own choosing.
 *
 * @throws {Error} if the chatbot in use is not a host we will talk to.
 */
export async function fetchChatbotHealth(signal?: AbortSignal): Promise<ChatbotHealth> {
  const base = normalizeChatbotUrl(resolveChatbotBaseUrl() || "");
  if (!base) throw new Error("That is not a chatbot host this build will talk to.");

  return withTimeout(signal, async (s) =>
    readJson<ChatbotHealth>(
      await fetch(`${base}${CHATBOT_HEALTH_PATH}`, { signal: s, headers: JSON_HEADERS })
    )
  );
}

/**
 * One row per Supabase branch — exactly one, whatever the chatbots say.
 *
 * A row is a DATABASE. Two chatbots reporting the same project used to produce two rows
 * with the same branch name, which read as a duplicate and left every pair the join did
 * not emit unreachable; the chatbot is a separate choice instead.
 */
function databaseRow(branch: BranchSummary): DatabaseTarget {
  // `credentials` is absent altogether while a branch is still provisioning — the
  // management API has no API keys to report yet. Default it here, at the one place a
  // branch enters a row, so the rest of the file can read the field: the join runs in
  // `useEnvironments`' render, where a TypeError takes the whole picker down and no
  // query error state ever reports it.
  const credentials = {
    url: branch.credentials?.url ?? "",
    anonKey: branch.credentials?.anonKey ?? "",
  };
  const ref = supabaseProjectRef(credentials.url);
  return {
    // A branch whose API keys could not be read has no URL, so no ref to key on.
    key: ref ?? `branch:${branch.id}`,
    name: branch.name,
    ref,
    branch: { ...branch, credentials },
  };
}

/** Default branch first, then alphabetical. */
function byDefaultThenName(a: DatabaseTarget, b: DatabaseTarget): number {
  // Normalise to booleans first: a branch that omits the field reports `undefined`, which
  // is neither equal to `false` nor truthy, so comparing the raw values returns 1 for both
  // orderings of the same pair and the sort loses its ordering contract.
  const aDefault = a.branch.isDefault === true;
  const bDefault = b.branch.isDefault === true;
  if (aDefault !== bDefault) return aDefault ? -1 : 1;
  return a.name.localeCompare(b.name);
}

/** The databases the app can be pointed at — the develop project and every preview
 * branch, once each. */
export function listDatabases(branches: BranchSummary[]): DatabaseTarget[] {
  return branches.map(databaseRow).sort(byDefaultThenName);
}

/**
 * The chatbots any of those databases can be paired with: the develop one, then every
 * sandbox chatbot the registry reports.
 *
 * An unreachable or empty registry is not a failure here — the develop chatbot alone is
 * a working list, and it is the one every preview build is already wired to. A chatbot
 * served from a host we would refuse to authenticate against is dropped instead: we
 * would not talk to it, so offering it would be a dead end.
 */
export function listChatbots(sandboxes: ChatbotTarget[]): ChatbotTarget[] {
  const vetted = sandboxes.flatMap((sandbox) => {
    const url = normalizeChatbotUrl(sandbox.url);
    return url ? [{ ...sandbox, url }] : [];
  });
  return [DEVELOP_CHATBOT, ...vetted];
}

/** Why this environment cannot be selected, or null when it can. */
export function environmentBlocker(env: Environment): string | null {
  // The pairs this module builds carry an allowlisted URL already; this is the guard for
  // one built any other way, since the app hands the chatbot a live access token.
  if (!normalizeChatbotUrl(env.chatbot.url)) {
    return "This chatbot is not on a host this build will hand an access token to.";
  }
  return branchBlocker(env.branch);
}

function branchBlocker(branch: BranchSummary): string | null {
  // A dead database is a dead database whether or not it is the default one — this check
  // comes first so the row cannot advertise "database PAUSED" next to an enabled button.
  if (branch.status === "PAUSED" || branch.status === "FAILED") {
    return `Branch is ${branch.status}.`;
  }
  // Applying the default branch CLEARS the override rather than writing its URL, so the
  // credentials below are not read and need not be present.
  if (branch.isDefault) return null;
  if (!branch.credentials.url || !branch.credentials.anonKey) {
    return "Branch credentials are unavailable.";
  }
  // Selecting this writes the URL into SUPABASE_OVERRIDE, and the next load builds the auth
  // client from it — the client the user's password is typed into. Only a Supabase project.
  if (!isSupabaseProjectUrl(branch.credentials.url)) {
    return "Branch API URL is not a Supabase project URL.";
  }
  return null;
}

function writeChatbotOverride(url: string | null): void {
  const normalized = url ? normalizeChatbotUrl(url) : null;
  if (normalized) localStorage.setItem(CHATBOT_OVERRIDE_KEY, normalized);
  else localStorage.removeItem(CHATBOT_OVERRIDE_KEY);
}

/** The branch override an environment implies, or null for the default project. */
function branchOverrideFor(env: Environment): SupabaseBranchOverride | null {
  if (env.branch.isDefault) return null;
  return {
    url: env.branch.credentials.url,
    anonKey: env.branch.credentials.anonKey,
    branchName: env.branch.name,
  };
}

function writeSupabaseOverride(override: SupabaseBranchOverride | null): void {
  if (override) localStorage.setItem(SUPABASE_OVERRIDE_KEY, JSON.stringify(override));
  else localStorage.removeItem(SUPABASE_OVERRIDE_KEY);
}

function restoreSupabaseOverride(previous: string | null): void {
  if (previous === null) localStorage.removeItem(SUPABASE_OVERRIDE_KEY);
  else localStorage.setItem(SUPABASE_OVERRIDE_KEY, previous);
}

/**
 * Point the app at an environment: database and chatbot together.
 *
 * Both halves always move, because the pair names both — picking them separately is not
 * applying them separately, and there is still no way in that writes one override alone.
 * The caller reloads afterwards; both overrides are read at module init.
 *
 * @throws {Error} if the switcher is disabled, or the environment is not selectable.
 */
export function applyEnvironment(env: Environment): void {
  if (!isChatbotOverrideEnabled()) {
    throw new Error("The environment switcher is disabled in this build.");
  }
  const blocker = environmentBlocker(env);
  if (blocker) throw new Error(blocker);

  // The two halves are two localStorage writes, and the second can fail on its own
  // (quota, private mode). Half-applied is the exact state this picker exists to
  // prevent — the new database with the old chatbot still attached — so put the
  // previous environment back before the caller sees the error.
  const previous = localStorage.getItem(SUPABASE_OVERRIDE_KEY);
  try {
    writeSupabaseOverride(branchOverrideFor(env));
    writeChatbotOverride(env.chatbot.url);
  } catch (cause) {
    restoreSupabaseOverride(previous);
    throw cause;
  }
}

/** Drop both overrides and go back to whatever the build was configured with. */
export function clearEnvironmentOverrides(): void {
  localStorage.removeItem(SUPABASE_OVERRIDE_KEY);
  localStorage.removeItem(CHATBOT_OVERRIDE_KEY);
}

/**
 * Compare the database the chatbot reports against the one the app is using.
 *
 * `/health` reports the chatbot's BOOT database, and that only decides where answers come
 * from when the chatbot is pinned to it — i.e. `ENVIRONMENT=production`. Everywhere else it
 * derives the project per request from the caller's token, so a differing boot ref is the
 * documented normal case (the shared chat-dev pod serving an app on a preview branch), not a
 * fault. Calling that a mismatch would paint a permanent red alarm on the mainline
 * configuration, and an alarm that is always on is one nobody reads.
 *
 * Equal refs are a genuine match under both models: pinned, it serves that project; routed,
 * the app's token names the same project. Everything `/health` cannot answer stays unknown —
 * a chatbot that predates the endpoint (404) is not a match, it is unchecked. A chatbot whose
 * own two settings disagree (`ref_mismatch`) is broken whatever the app is doing, so that
 * verdict wins.
 */
export function describePairing(health: ChatbotHealth | null, appRef: string | null): Pairing {
  if (!health) return { kind: "unknown", reason: "The chatbot did not report its database." };
  const chatbotRef = health.supabase_ref;
  if (!chatbotRef) {
    return { kind: "unknown", reason: "This chatbot does not report a Supabase project." };
  }
  if (health.ref_mismatch) return { kind: "self-mismatch", chatbotRef };
  if (!appRef) return { kind: "unknown", reason: "The app's Supabase project is unknown." };
  if (chatbotRef === appRef) return { kind: "match", ref: chatbotRef };
  return health.environment === "production"
    ? { kind: "mismatch", chatbotRef, appRef }
    : { kind: "per-request", chatbotRef, appRef };
}

/**
 * Why pairing `chatbot` with the database `ref` would be a mismatch, or null when it
 * would not be one.
 *
 * The verdict for a pair being OFFERED, drawn from the registry, where
 * {@link describePairing} judges the pair in USE, drawn from `/health` — one set of
 * verdicts, two sources.
 *
 * `routesPerToken` is the deployment fact, taken from the chatbot currently in use:
 * routing is switched on by cluster secrets shared across every chatbot in the
 * environment, so if the one we can see follows the caller's token, its siblings do
 * too. When it is on, a differing boot project is not a mismatch at all — the chatbot
 * answers from whichever database the app's token names.
 *
 * This used to be hardcoded as "a sandbox chatbot serves only the project it booted
 * on". That was true only while SUPABASE_ACCESS_TOKEN was missing from the cluster
 * secret, and the moment it was added the picker started showing a red DIFFERENT
 * DATABASE warning directly underneath its own card saying the chatbot routes from
 * your token. Read the fact; do not infer it.
 */
export function pairingMismatch(
  chatbot: ChatbotTarget,
  ref: string | null,
  routesPerToken?: boolean
): Pairing | null {
  const chatbotRef = chatbot.supabase_ref;
  if (!chatbotRef || !ref || chatbotRef === ref) return null;
  if (routesPerToken) return null;
  return { kind: "mismatch", chatbotRef, appRef: ref };
}
